import get from 'lodash/get'
import Vue from 'vue'
import axios from 'axios'
import { actionTypes as schoolActionTypes } from './school'
import defaultUserAvatar from '~/assets/images/default-avatar.png'

import {
  GET_USER_PROFILE,
  GET_ACADEMY_LEARNING_JOURNEY,
  FOUR_LOGIN,
  GET_LEARNING_STREAK,
  CHECK_AUTHENTICATION,
} from '~/graphql/queries/users'
import { USER_ROLES } from '~/global/data/roles'
import { MODULES } from '~/utils/const'
import { isHasRoleInHQ } from '~/utils/role'

const mutationTypes = {
  SET_FETCHING_USER_PROFILE: 'SET_FETCHING_USER_PROFILE',
  SET_FETCH_USER_PROFILE_ERROR: 'SET_FETCH_USER_PROFILE_ERROR',
  SET_USER_PROFILE: 'SET_USER_PROFILE',
  UPDATE_USER_PROFILE: 'UPDATE_USER_PROFILE',
  SET_WORK_EXPERIENCES: 'SET_WORK_EXPERIENCES',
  ADD_NEW_USER_WORK_EXPERIENCE: 'ADD_NEW_USER_WORK_EXPERIENCE',
  UPDATE_USER_WORK_EXPERIENCE: 'UPDATE_USER_WORK_EXPERIENCE',
  REMOVE_USER_WORK_EXPERIENCE: 'REMOVE_USER_WORK_EXPERIENCE',
  SET_TRAINING_RECORDS: 'SET_TRAINING_RECORDS',
  ADD_NEW_USER_TRAINING_RECORD: 'ADD_NEW_USER_TRAINING_RECORD',
  UPDATE_USER_TRAINING_RECORD: 'UPDATE_USER_TRAINING_RECORD',
  REMOVE_USER_TRAINING_RECORD: 'REMOVE_USER_TRAINING_RECORD',
  SET_FETCHING_LEARNING_JOURNEY: 'SET_FETCHING_LEARNING_JOURNEY',
  SET_FETCH_LEARNING_JOURNEY_ERROR: 'SET_FETCH_LEARNING_JOURNEY_ERROR',
  SET_LEARNING_JOURNEY: 'SET_LEARNING_JOURNEY',
  UPDATE_FAVORITE_COURSE: 'UPDATE_FAVORITE_COURSE',
  SET_PERSONAL_PROFILES: 'SET_PERSONAL_PROFILES',
}

const actionTypes = {
  FETCH_USER_PROFILE: 'FETCH_USER_PROFILE',
  REFRESH_USER_TOKEN: 'REFRESH_USER_TOKEN',
  FETCH_LEARNING_JOURNEY: 'FETCH_LEARNING_JOURNEY',
  FOUR_LOGIN: 'FOUR_LOGIN',
  GET_USER_PROFILE_SIX: 'GET_USER_PROFILE_SIX',
  GET_LEARNING_STREAK: 'GET_LEARNING_STREAK',
  CHECK_AUTHENTICATION: 'CHECK_AUTHENTICATION',
}

/* 
  role of user include [EDUCATOR, OWNER, PRINCIPAL, STAFF]
*/

export const state = () => ({
  personalProfiles: [],
  fetchingProfile: false,
  fetchProfileError: false,
  academyLearningJourney: {
    fetching: false,
    fetchError: false,
    learningJourney: {
      addSchoolDetails: false,
      addStaff: false,
      choosePerformanceFramework: false,
      grantTeacherPermission: false,
    },
  },
  userProfile: {
    id: '',
    name: '',
    email: '',
    designation: '',
    dateJoin: '',
    qualificationLevels: '',
    yearOfExp: '',
    agesFrom: '',
    agesTo: '',
    role: '',
    academyMinutesPerDay: 0,
    academyActivedAt: null,
    courses: [],
    media: {
      url: defaultUserAvatar,
      fileName: '',
    },
    school: {
      address: '',
      agesFrom: '',
      agesTo: '',
      id: '',
      name: '',
      population: '',
      schoolProfiles: [],
      staffSize: '',
      website: '',
    },
  },
  workExperiences: [],
  trainingRecords: [],
  minutesPerDayOptions: [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '15', value: 15 },
    { label: '20', value: 20 },
    { label: '25', value: 25 },
    { label: '30', value: 30 },
    { label: '35', value: 35 },
    { label: '40', value: 40 },
    { label: '45', value: 45 },
    { label: '50', value: 50 },
    { label: '55', value: 55 },
    { label: '60', value: 60 },
  ],
})

export const mutations = {
  [mutationTypes.UPDATE_FAVORITE_COURSE](state, courses) {
    state.userProfile.courses = courses
  },
  [mutationTypes.SET_FETCHING_USER_PROFILE](state, fetchingProfile) {
    state.fetchingProfile = fetchingProfile
  },
  [mutationTypes.SET_FETCH_USER_PROFILE_ERROR](state, fetchProfileError) {
    state.fetchProfileError = fetchProfileError
  },
  [mutationTypes.SET_USER_PROFILE](state, userProfile) {
    state.userProfile = userProfile
    state.userProfile.role = get(userProfile, 'title', USER_ROLES.STAFF)
  },
  [mutationTypes.UPDATE_USER_PROFILE](state, userProfile) {
    state.userProfile = Object.assign({}, state.userProfile, userProfile)
  },
  [mutationTypes.SET_WORK_EXPERIENCES](state, workExperiences) {
    state.workExperiences = workExperiences
  },
  [mutationTypes.ADD_NEW_USER_WORK_EXPERIENCE](state, workExperience) {
    state.workExperiences.push(workExperience)
  },
  [mutationTypes.UPDATE_USER_WORK_EXPERIENCE](state, workExperience) {
    const indexWorkExperience = state.workExperiences.findIndex((w) => +w.id === +workExperience.id)
    if (indexWorkExperience > -1) {
      Vue.set(state.workExperiences, indexWorkExperience, workExperience)
    }
  },
  [mutationTypes.REMOVE_USER_WORK_EXPERIENCE](state, workExperienceId) {
    state.workExperiences = state.workExperiences.filter((w) => +w.id !== +workExperienceId)
  },
  [mutationTypes.SET_TRAINING_RECORDS](state, trainingRecords) {
    state.trainingRecords = trainingRecords
  },
  [mutationTypes.ADD_NEW_USER_TRAINING_RECORD](state, trainingRecord) {
    state.trainingRecords.push(trainingRecord)
  },
  [mutationTypes.UPDATE_USER_TRAINING_RECORD](state, trainingRecord) {
    const indexTrainingRecord = state.trainingRecords.findIndex((record) => +record.id === +trainingRecord.id)
    if (indexTrainingRecord > -1) {
      Vue.set(state.trainingRecords, indexTrainingRecord, trainingRecord)
    }
  },
  [mutationTypes.REMOVE_USER_TRAINING_RECORD](state, trainingRecordId) {
    state.trainingRecords = state.trainingRecords.filter((record) => +record.id !== +trainingRecordId)
  },
  [mutationTypes.SET_LEARNING_JOURNEY](state, learningJourney) {
    state.academyLearningJourney.learningJourney = learningJourney
  },
  [mutationTypes.SET_FETCHING_LEARNING_JOURNEY](state, fetching) {
    state.academyLearningJourney.fetching = fetching
  },
  [mutationTypes.SET_FETCH_LEARNING_JOURNEY_ERROR](state, fetchError) {
    state.academyLearningJourney.fetchError = fetchError
  },
  [mutationTypes.SET_PERSONAL_PROFILES](state, personalProfiles) {
    state.personalProfiles = personalProfiles
  },
}

export const actions = {
  async [actionTypes.REFRESH_USER_TOKEN](_) {
    try {
      const internalToken = this.app.$cookie.get('laAccessToken')
      const refreshToken = this.app.$cookie.get('laRefreshToken')
      const externalToken = this.app.$cookie.get('accessToken')

      // Handle internal token refresh
      if (internalToken && refreshToken) {
        const params = {
          refresh_token: refreshToken,
        }

        const { data } = await axios.post(`${process.env.NUXT_ENV_ACADEMY_AUTH_ENDPOINT}/token`, params, {
          headers: { 'Content-Type': 'application/json' },
        })

        if (data && data.access_token) {
          await this.app.$apolloHelpers.onLogin(data.access_token, undefined)
          this.app.$cookie.set('laAccessToken', data.access_token)
          return data
        }
        throw data
      }

      // No valid token to refresh
      return null
    } catch (error) {
      console.error('REFRESH_USER_TOKEN', error)
    }
  },

  async [actionTypes.FOUR_LOGIN]({ commit, dispatch }, jwtToken) {
    try {
      const academyClient = this.app.apolloProvider.defaultClient
      const { data } = await academyClient.mutate({
        mutation: FOUR_LOGIN,
        variables: { client: '4.0', jwtToken },
      })
      if (data) {
        const token = data.fourLogIn.sessionTokens.userToken
        await this.app.$apolloHelpers.onLogin(token)
        this.app.$cookie.set('laAccessToken', token)
        return { status: 'success', data }
      }
      return { status: 'failed' }
    } catch (error) {
      return { errors: error, status: 'failed' }
    }
  },
  async [actionTypes.FETCH_USER_PROFILE]({ commit, dispatch, rootState }) {
    try {
      commit(mutationTypes.SET_FETCHING_USER_PROFILE, true)
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: GET_USER_PROFILE,
        fetchPolicy: 'no-cache',
      })
      if (data.academyUserProfile) {
        const { personalProfiles, trainingRecords, workExperiences } = data.academyUserProfile
        if (Array.isArray(personalProfiles) && personalProfiles.length > 0) {
          let schoolId = this.$cookie.get('schoolId')

          const schools = get(rootState.school, 'schoolTree', [])
          if (!schoolId && schools.length > 0) {
            schoolId = schools[0].id
          }
          let userProfile = personalProfiles.find((profile) => get(profile, 'school.id', null) === parseInt(schoolId))
          if (!userProfile)
            userProfile = personalProfiles.find((profile) => schools.map((item) => item.id).includes(profile.school.id))
          const userMedia = get(userProfile, 'media', {
            url: defaultUserAvatar,
            fileName: '',
          })
          const userProfileSchoolId = get(userProfile, 'school.id', null)

          commit(mutationTypes.SET_USER_PROFILE, Object.assign({}, userProfile, { media: userMedia }))

          await dispatch(
            `school/${schoolActionTypes.FETCH_SCHOOL_PROFILE}`,
            { id: schoolId || +userProfileSchoolId },
            { root: true }
          )
        }
        commit(mutationTypes.SET_TRAINING_RECORDS, trainingRecords)
        commit(mutationTypes.SET_WORK_EXPERIENCES, workExperiences)
        commit(mutationTypes.SET_FETCHING_USER_PROFILE, false)
        commit(mutationTypes.SET_FETCH_USER_PROFILE_ERROR, false)
        commit(mutationTypes.SET_PERSONAL_PROFILES, personalProfiles)
        return data
      }
      throw data
    } catch (error) {
      console.error('FETCH_USER_PROFILE', error)
      commit(mutationTypes.SET_FETCHING_USER_PROFILE, false)
      commit(mutationTypes.SET_FETCH_USER_PROFILE_ERROR, true)
    }
  },
  async [actionTypes.FETCH_LEARNING_JOURNEY]({ commit, state }) {
    try {
      const { school } = state.userProfile
      if (!school.id) {
        throw new Error('Can not find school info!')
      }
      const client = this.app.apolloProvider.defaultClient
      const {
        data: { academyLearningJourney },
      } = await client.query({
        query: GET_ACADEMY_LEARNING_JOURNEY,
        variables: { schoolId: school.id },
        fetchPolicy: 'no-cache',
      })
      if (academyLearningJourney) {
        commit(mutationTypes.SET_LEARNING_JOURNEY, academyLearningJourney)
        commit(mutationTypes.SET_FETCHING_LEARNING_JOURNEY, false)
        commit(mutationTypes.SET_FETCH_LEARNING_JOURNEY_ERROR, false)
        return academyLearningJourney
      }
    } catch (error) {
      commit(mutationTypes.SET_FETCHING_LEARNING_JOURNEY, false)
      commit(mutationTypes.SET_FETCH_LEARNING_JOURNEY_ERROR, true)
      throw error
    }
  },
  async [actionTypes.GET_LEARNING_STREAK]({ commit, state }) {
    try {
      const client = this.app.apolloProvider.defaultClient
      const { school } = state.userProfile
      const response = await client.query({
        query: GET_LEARNING_STREAK,
        variables: { schoolId: school.id, days: 7 },
        fetchPolicy: 'no-cache',
      })
      return response.data.academyUserStreak
    } catch (error) {
      console.error('GET_LEARNING_STREAK', error)
      throw error
    }
  },
  async [actionTypes.CHECK_AUTHENTICATION](_) {
    try {
      const client = this.app.apolloProvider.defaultClient
      await client.query({
        query: CHECK_AUTHENTICATION,
      })
    } catch (error) {
      console.error('CHECK_AUTHENTICATION', error)
      throw error
    }
  },
}

export const getters = {
  userProfile: (state) => state.userProfile,
  currentUserId: (state) => state.userProfile.userId,
  currentPersonalProfileId: (state) => {
    return get(state.userProfile, 'user.personalProfiles[0].id', null)
  },
  currentPersonalProfileIdBySelectedSchool: (state, _, __, rootGetters) => {
    const userProfileBySelectedSchool = state.personalProfiles.find(
      (profile) => get(profile, 'school.id', null) === rootGetters['school/schoolId']
    )
    const foundProfileHQSchool =
      state.personalProfiles.find((profile) => profile.type === 'hq') || state.personalProfiles[0]
    return userProfileBySelectedSchool?.id || foundProfileHQSchool?.id || null
  },
  personalProfiles: (state) => state.personalProfiles,
  userRole: (state) => state.userProfile.role,
  allUserRoles: (state, getters, rootState, rootGetters) => {
    const { userRoles } = get(state.userProfile, 'user', {})
    return userRoles
      ? userRoles.filter((role) => !role.endDate && role.schoolId === rootGetters['school/schoolId'])
      : []
  },
  allUserRolesIncludeHQ: (state, getters, rootState, rootGetters) => {
    const currentSchool = rootGetters['school/schoolProfile']
    const userRoles = []
    const findAllUserRolesIncludeHQ = (school) => {
      if (!school) return
      const foundPersonalProfile = getters.personalProfiles.find((item) => item.school?.id === school.id)
      if (foundPersonalProfile) {
        const roles = get(foundPersonalProfile, 'user.userRoles', []).filter(
          (role) => !role.endDate && role.schoolId === school.id
        )
        userRoles.push(...roles)
      }
      if (school.parentId) {
        const foundSchool = rootGetters['school/schoolTree'].find((item) => item.id === school.parentId)
        if (foundSchool) findAllUserRolesIncludeHQ(foundSchool)
      }
    }
    findAllUserRolesIncludeHQ(currentSchool)
    return userRoles
  },
  academyRole: (state, getters, rootState, rootGetters) => {
    const found = get(state.userProfile, 'user.userRoles', [])?.find(
      (role) => role.module === MODULES.ACADEMY && !role.endDate && role.schoolId === rootGetters['school/schoolId']
    )
    return found ? found.name : null
  },
  performanceRole: (state, getters, rootState, rootGetters) => {
    const found = get(state.userProfile, 'user.userRoles', [])?.find(
      (role) => role.module === MODULES.PERFORMANCE && !role.endDate && role.schoolId === rootGetters['school/schoolId']
    )
    return found ? found.name : null
  },
  administrationRole: (state, getters, rootState, rootGetters) => {
    const found = get(state.userProfile, 'user.userRoles', [])?.find(
      (role) =>
        role.module === MODULES.ADMINISTRATION && !role.endDate && role.schoolId === rootGetters['school/schoolId']
    )
    return found ? found.name : null
  },
  isSuper: (state) => {
    const userRoles = get(state.userProfile, 'user.userRoles') || []
    return userRoles.some((role) => role.name === USER_ROLES.SUPER)
  },
  academyLearningJourney: (state) => state.academyLearningJourney,
  completedGetStarted(state) {
    const {
      addSchoolDetails,
      addStaff,
      choosePerformanceFramework,
      grantTeacherPermission,
      activeLittleAcademy,
      completeProfile,
      enrolledAClass,
    } = state.academyLearningJourney.learningJourney
    if (state.userProfile.role === 'OWNER') {
      return addSchoolDetails && addStaff && choosePerformanceFramework && grantTeacherPermission
    } else {
      return activeLittleAcademy && completeProfile && enrolledAClass
    }
  },
  workExperiences: (state) => state.workExperiences,
  trainingRecords: (state) => state.trainingRecords,
  fetchingProfile: (state) => state.fetchingProfile,
  userAvatar: (state) => get(state, 'userProfile.media.url', defaultUserAvatar),
  minutesPerDayOptions: (state) => state.minutesPerDayOptions,
  isTrainee: (state, getters, rootState, rootGetters) => {
    return getters.academyRole === USER_ROLES.ACADEMY_TRAINEE || getters.academyRole === USER_ROLES.TRAINEE
  },
  isAcademyManager: (state, getters, rootState, rootGetters) => {
    if (getters.academyRole === USER_ROLES.ACADEMY_MANAGER || getters.isSuper) return true
    return isHasRoleInHQ(getters.personalProfiles, rootGetters, MODULES.ACADEMY, USER_ROLES.ACADEMY_MANAGER)
  },
  isAcademyTrainee: (state, getters, rootState, rootGetters) => {
    if (getters.academyRole === USER_ROLES.ACADEMY_TRAINEE || getters.isSuper) return true
    return isHasRoleInHQ(getters.personalProfiles, rootGetters, MODULES.ACADEMY, USER_ROLES.ACADEMY_TRAINEE)
  },
  isTrainingManager: (state, getters, rootState, rootGetters) => {
    if (getters.academyRole === USER_ROLES.TRAINING_MANAGER || getters.isSuper) return true
    return isHasRoleInHQ(getters.personalProfiles, rootGetters, MODULES.ACADEMY, USER_ROLES.TRAINING_MANAGER)
  },
  isPerformanceManager: (state, getters, rootState, rootGetters) => {
    if (getters.performanceRole === USER_ROLES.PERFORMANCE_MANAGER || getters.isSuper) return true
    return isHasRoleInHQ(getters.personalProfiles, rootGetters, MODULES.PERFORMANCE, USER_ROLES.PERFORMANCE_MANAGER)
  },
  isAdministrator: (state, getters, rootState, rootGetters) => {
    if (getters.administrationRole === USER_ROLES.ADMINISTRATOR || getters.isSuper) return true
    return isHasRoleInHQ(getters.personalProfiles, rootGetters, MODULES.ADMINISTRATION, USER_ROLES.ADMINISTRATOR)
  },
  isManager(state, getters) {
    return (
      getters.isAcademyManager || getters.isTrainingManager || getters.isPerformanceManager || getters.isAdministrator
    )
  },
}
